export const SUBSCRIPTION_STATUSES = ['active', 'failed', 'cancelled', 'expired'] as const;

export type SubscriptionStatus = (typeof SUBSCRIPTION_STATUSES)[number];

export const RENEWAL_MODES = ['oefy', 'shop', 'none'] as const;

export type RenewalMode = (typeof RENEWAL_MODES)[number];

export const BILLING_INTERVALS = ['month', 'quarter', 'half_year', 'year'] as const;

export type BillingInterval = (typeof BILLING_INTERVALS)[number];

export const BILLING_INTERVAL_MONTHS: Readonly<Record<BillingInterval, number>> = {
  month: 1,
  quarter: 3,
  half_year: 6,
  year: 12,
};

export const CANCELLATION_REASONS = [
  'too_expensive',
  'rarely_used',
  'not_motivating',
  'content_mismatch',
  'technical_problems',
  'goal_reached',
  'other_product',
  'other',
] as const;

export type CancellationReason = (typeof CANCELLATION_REASONS)[number];

export const CANCELLATION_TEXT_MAX = 1000;

export interface SubscriptionState {
  readonly status: SubscriptionStatus;
  readonly paidUntil: Date;
  readonly graceUntil: Date | null;
  readonly cancelledAt: Date | null;
  readonly endedAt: Date | null;
}

export type SubscriptionEvent =
  | { readonly type: 'payment_succeeded'; readonly periodEnd: Date }
  | { readonly type: 'payment_failed' }
  | { readonly type: 'cancellation_requested' }
  | { readonly type: 'cancellation_withdrawn' }
  | { readonly type: 'payment_reversed' }
  | { readonly type: 'replaced' }
  | { readonly type: 'time_passed' };

export const SUBSCRIPTION_EFFECTS = [
  'ensure_provider_subscription',
  'stop_provider_subscription',
  'send_welcome',
  'send_renewal_receipt',
  'send_payment_failed_notice',
  'send_cancellation_confirmation',
  'send_access_ended_notice',
  'place_book_order',
  'send_trial_reminder',
] as const;

export type SubscriptionEffect = (typeof SUBSCRIPTION_EFFECTS)[number];

export const BOOK_ORDER_STATUSES = ['pending', 'placed', 'failed'] as const;

export type BookOrderStatus = (typeof BOOK_ORDER_STATUSES)[number];

export const SHIPPING_COUNTRIES = ['NL', 'BE'] as const;

export type ShippingCountry = (typeof SHIPPING_COUNTRIES)[number];

const POSTCODES: Readonly<Record<ShippingCountry, RegExp>> = {
  NL: /^([1-9][0-9]{3}) ?([A-Z]{2})$/,
  BE: /^([1-9][0-9]{3})$/,
};

export function normalizePostcode(country: ShippingCountry, value: string): string | null {
  const match = POSTCODES[country].exec(value.trim().toUpperCase());
  if (!match) {
    return null;
  }
  return country === 'NL' ? `${match[1] ?? ''} ${match[2] ?? ''}` : (match[1] ?? null);
}

export interface SubscriptionPolicy {
  readonly gracePeriodDays: number;
}

export interface SubscriptionTransition {
  readonly state: SubscriptionState;
  readonly effects: readonly SubscriptionEffect[];
  readonly changed: boolean;
}

export const DEFAULT_SUBSCRIPTION_POLICY: SubscriptionPolicy = { gracePeriodDays: 7 };

const DAY_IN_MS = 86_400_000;

function unchanged(state: SubscriptionState): SubscriptionTransition {
  return { state, effects: [], changed: false };
}

function moveTo(
  state: SubscriptionState,
  effects: readonly SubscriptionEffect[] = [],
): SubscriptionTransition {
  return { state, effects, changed: true };
}

function isBefore(moment: Date, other: Date): boolean {
  return moment.getTime() < other.getTime();
}

function laterOf(first: Date, second: Date): Date {
  return isBefore(first, second) ? second : first;
}

function onPaymentSucceeded(state: SubscriptionState, periodEnd: Date): SubscriptionTransition {
  const paidUntil = laterOf(state.paidUntil, periodEnd);
  const extended = paidUntil !== state.paidUntil;
  switch (state.status) {
    case 'cancelled':
      return extended ? moveTo({ ...state, paidUntil }) : unchanged(state);
    case 'expired':
      return moveTo(
        {
          ...state,
          status: 'active',
          paidUntil,
          graceUntil: null,
          cancelledAt: null,
          endedAt: null,
        },
        ['ensure_provider_subscription'],
      );
    case 'failed':
      return moveTo({ ...state, status: 'active', paidUntil, graceUntil: null }, [
        'ensure_provider_subscription',
      ]);
    case 'active':
      return extended ? moveTo({ ...state, paidUntil }) : unchanged(state);
  }
}

function onTimePassed(state: SubscriptionState, now: Date): SubscriptionTransition {
  if (state.status === 'cancelled' && !isBefore(now, state.paidUntil)) {
    return moveTo({ ...state, status: 'expired', endedAt: now }, ['send_access_ended_notice']);
  }
  if (state.status === 'failed' && state.graceUntil && !isBefore(now, state.graceUntil)) {
    return moveTo({ ...state, status: 'expired', graceUntil: null, endedAt: now }, [
      'stop_provider_subscription',
      'send_access_ended_notice',
    ]);
  }
  return unchanged(state);
}

function daysInMonth(year: number, month: number): number {
  return new Date(Date.UTC(year, month + 1, 0)).getUTCDate();
}

export function addBillingInterval(start: Date, interval: BillingInterval, count = 1): Date {
  const months = BILLING_INTERVAL_MONTHS[interval] * count;
  const target = start.getUTCMonth() + months;
  const year = start.getUTCFullYear() + Math.floor(target / 12);
  const month = ((target % 12) + 12) % 12;
  const day = Math.min(start.getUTCDate(), daysInMonth(year, month));
  return new Date(
    Date.UTC(
      year,
      month,
      day,
      start.getUTCHours(),
      start.getUTCMinutes(),
      start.getUTCSeconds(),
      start.getUTCMilliseconds(),
    ),
  );
}

export function renewalPeriodEnd(
  state: SubscriptionState,
  interval: BillingInterval,
  paidAt: Date,
): Date {
  const start = state.status === 'expired' ? laterOf(paidAt, state.paidUntil) : state.paidUntil;
  return addBillingInterval(start, interval);
}

export function applySubscriptionEvent(
  state: SubscriptionState,
  event: SubscriptionEvent,
  now: Date,
  policy: SubscriptionPolicy = DEFAULT_SUBSCRIPTION_POLICY,
): SubscriptionTransition {
  switch (event.type) {
    case 'payment_succeeded':
      return onPaymentSucceeded(state, event.periodEnd);
    case 'payment_failed':
      return state.status === 'active'
        ? moveTo(
            {
              ...state,
              status: 'failed',
              graceUntil: new Date(now.getTime() + policy.gracePeriodDays * DAY_IN_MS),
            },
            ['send_payment_failed_notice'],
          )
        : unchanged(state);
    case 'cancellation_requested':
      return state.status === 'active' || state.status === 'failed'
        ? moveTo({ ...state, status: 'cancelled', graceUntil: null, cancelledAt: now }, [
            'stop_provider_subscription',
            'send_cancellation_confirmation',
          ])
        : unchanged(state);
    case 'cancellation_withdrawn':
      return state.status === 'cancelled' &&
        state.endedAt === null &&
        state.paidUntil.getTime() > now.getTime()
        ? moveTo({ ...state, status: 'active', cancelledAt: null }, [
            'ensure_provider_subscription',
          ])
        : unchanged(state);
    case 'payment_reversed':
      if (state.status === 'expired') {
        return unchanged(state);
      }
      return moveTo(
        { ...state, status: 'expired', graceUntil: null, endedAt: now },
        state.status === 'cancelled'
          ? ['send_access_ended_notice']
          : ['stop_provider_subscription', 'send_access_ended_notice'],
      );
    case 'replaced':
      if (state.status === 'active') {
        return moveTo({ ...state, status: 'cancelled', graceUntil: null, cancelledAt: now }, [
          'stop_provider_subscription',
        ]);
      }
      return state.status === 'failed'
        ? moveTo({ ...state, status: 'expired', graceUntil: null, endedAt: now }, [
            'stop_provider_subscription',
          ])
        : unchanged(state);
    case 'time_passed':
      return onTimePassed(state, now);
  }
}
